import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { FormInput, FormSelect } from '../finance';
import { X, Plus, Trash, CircleNotch } from '@phosphor-icons/react';
import transportService from '../../services/transportService';
import './transport-forms.css';

const ROUTE_STATUS_OPTIONS = [
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
  { value: 'suspended', label: 'Suspended' }
];

function RouteForm({ route, onSave, onClose }) {
  const [formData, setFormData] = useState(route || {
    route_name: '',
    route_code: '',
    start_point: '',
    end_point: '',
    distance_km: 0,
    estimated_duration_minutes: 0,
    pickup_time: '',
    dropoff_time: '',
    pickup_days: '[]',
    intermediate_stops: '[]',
    fee_amount: 0,
    status: 'active',
    notes: ''
  });
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedDays, setSelectedDays] = useState([]);
  const [stops, setStops] = useState([]);
  const [stopInput, setStopInput] = useState('');

  const days = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

  useEffect(() => {
    const loadVehicles = async () => {
      try {
        const data = await transportService.listVehicles();
        setVehicles(Array.isArray(data) ? data : data.vehicles || []);
      } catch (error) {
        console.error('Failed to load vehicles');
      }
    };
    loadVehicles();

    if (route && route.pickup_days) {
      try {
        setSelectedDays(typeof route.pickup_days === 'string' ? JSON.parse(route.pickup_days) : route.pickup_days);
      } catch (e) {
        setSelectedDays([]);
      }
    }

    if (route && route.intermediate_stops) {
      try {
        const stopsArray = typeof route.intermediate_stops === 'string' ? JSON.parse(route.intermediate_stops) : route.intermediate_stops;
        setStops(Array.isArray(stopsArray) ? stopsArray : []);
      } catch (e) {
        setStops([]);
      }
    }
  }, [route]);

  const handleChange = (e) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'number' ? parseFloat(value) : value
    }));
  };

  const toggleDay = (day) => {
    setSelectedDays(prev =>
      prev.includes(day) ? prev.filter(d => d !== day) : [...prev, day]
    );
  };

  const addStop = () => {
    if (stopInput.trim()) {
      setStops([...stops, stopInput]);
      setStopInput('');
    }
  };

  const removeStop = (index) => {
    setStops(stops.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.route_name || !formData.route_code) {
      toast.error('Route name and code are required');
      return;
    }

    const submitData = {
      ...formData,
      pickup_days: JSON.stringify(selectedDays),
      intermediate_stops: JSON.stringify(stops)
    };

    setLoading(true);
    try {
      await onSave(route?.id, submitData);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal modal-lg">
        <div className="modal-header">
          <h2>{route ? 'Edit Route' : 'Add New Route'}</h2>
          <button onClick={onClose} className="btn-close"><X size={20} /></button>
        </div>

        <form onSubmit={handleSubmit} className="modal-body">
          <div className="form-row">
            <FormInput
              label="Route Code"
              name="route_code"
              value={formData.route_code}
              onChange={handleChange}
              required
              placeholder="e.g., RT001"
            />
            <FormInput
              label="Route Name"
              name="route_name"
              value={formData.route_name}
              onChange={handleChange}
              required
            />
          </div>

          <div className="form-row">
            <FormInput label="Start Point" name="start_point" value={formData.start_point} onChange={handleChange} required />
            <FormInput label="End Point" name="end_point" value={formData.end_point} onChange={handleChange} required />
          </div>

          <div className="form-row">
            <FormInput
              label="Distance (KM)"
              name="distance_km"
              type="number"
              value={formData.distance_km}
              onChange={handleChange}
              step="0.1"
            />
            <FormInput
              label="Estimated Duration (min)"
              name="estimated_duration_minutes"
              type="number"
              value={formData.estimated_duration_minutes}
              onChange={handleChange}
            />
          </div>

          <div className="form-row">
            <FormInput label="Pickup Time" name="pickup_time" type="time" value={formData.pickup_time} onChange={handleChange} />
            <FormInput label="Dropoff Time" name="dropoff_time" type="time" value={formData.dropoff_time} onChange={handleChange} />
          </div>

          <div className="form-group">
            <label>Pickup Days</label>
            <div className="days-selector">
              {days.map(day => (
                <label key={day} className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={selectedDays.includes(day)}
                    onChange={() => toggleDay(day)}
                  />
                  {day}
                </label>
              ))}
            </div>
          </div>

          <div className="form-group">
            <label>Intermediate Stops</label>
            <div className="stops-input">
              <FormInput
                value={stopInput}
                onChange={(e) => setStopInput(e.target.value)}
                placeholder="Enter stop name and press Add"
              />
              <button type="button" onClick={addStop} className="btn btn-secondary">
                <Plus size={16} /> Add
              </button>
            </div>
            {stops.length > 0 && (
              <div className="stops-list">
                {stops.map((stop, index) => (
                  <div key={index} className="stop-item">
                    <span>{stop}</span>
                    <button type="button" onClick={() => removeStop(index)} className="btn-icon btn-danger">
                      <Trash size={14} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="form-row">
            <FormInput
              label="Monthly Fee (GHS)"
              name="fee_amount"
              type="number"
              value={formData.fee_amount}
              onChange={handleChange}
              step="0.01"
            />
            <FormSelect
              label="Status"
              name="status"
              value={formData.status}
              onChange={handleChange}
              options={ROUTE_STATUS_OPTIONS}
            />
          </div>

          <FormInput
            label="Notes"
            name="notes"
            as="textarea"
            value={formData.notes}
            onChange={handleChange}
            rows="3"
          />

          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose} disabled={loading}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? (
                <span className="button-loading">
                  <CircleNotch size={16} weight="fill" className="spinner" />
                  Saving...
                </span>
              ) : route ? 'Update Route' : 'Create Route'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default RouteForm;
